import  React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { EyeOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Layout, Space, Table } from 'antd';
import useBrands from '../../gql/hooks/useBrands'; 
import useCertificates from '../../gql/hooks/useCertificates'; 
import Spinner from '../../components/Spinner/Spinner';
import useHasError from '../../hooks/useHasError'; 
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import useIsLoading from '../../hooks/useIsLoading'; 
import useSearchFilter from '../../hooks/useSearchFilter'; 
import SearchBar from '../../components/SearchBar/SearchBar';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import { Routes } from '../../../routes';
import { routeHistory } from '@nackle/biw-web-router';
import { flags } from '../../../featureFlags/flags'; 
import { useURLQueries } from '../../hooks/useURLQueries'; 
import './styles.less';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';

// Applied to every column except the greedy name column: keeps headers and
// cell contents on a single line so they aren't compressed into wrapping.
const noWrap = {
    onHeaderCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
    onCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
};

const CertificatesListPage = ( props ) => {
    const { brandsLoading, brandsError, getBrand } = useBrands();
    const { certificatesLoading, certificatesError, certificates } = useCertificates();
    const { currentPage, setCurrentPage, searchText, setSearchText, searchWords, 
        sortField, setSortField, sortDirection, setSortDirection } = useURLQueries();
    const { filter, filteredData } = useSearchFilter();
    const { isLoading } = useIsLoading( { items: [ brandsLoading, certificatesLoading ] } );
    const { hasError } = useHasError( { items: [ brandsError, certificatesError ] } );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Incentives', path: Routes.GamificationNav.buildPath() },
        { name: 'Certificates' }
    ];
 
    useEffect( () => {
        if ( !isLoading && !hasError && certificates && searchText ) {
            filter( searchText, certificates );
        }
    }, [ isLoading, hasError, certificates ] );

    useEffect( () => {
        if ( !isLoading && !hasError && certificates && searchText ) {
            filter( searchText, certificates );
        }
    }, [ isLoading, hasError, certificates ] );

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateCertificate.buildPath() );
    };

    const handlePreviewClick = ( url ) => {  
        window.open( url, '_blank' );
    };
 
    const handleEditClick = ( i ) => {
        routeHistory.push( Routes.EditCertificate.buildPath( i.id ) );  
    };

    const handleSearchClick = ( value ) => {
        setCurrentPage( 1 );
        setSortField( 'name' );
        setSortDirection( 'ascend' );
        setSearchText( value );
        filter( value, certificates );
    };

    const handleTableChange = ( pagination, filters, sorter ) => {
        setSortField( sorter?.columnKey );
        setSortDirection( sorter?.order );
    };

    const handlePaginatorChange = ( pagination ) => {
        setCurrentPage( pagination );
    };

    const columns =  [ 
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            width: '100%',
            sortOrder: sortField === 'name' && sortDirection,   
            sorter: ( a, b ) => a.name.localeCompare( b.name ),
            render: ( name ) => searchText ? renderWithSearchWords( name, searchWords ) : name
        },
        {
            key: 'profileName',
            dataIndex: 'profileName',
            title: 'Profile',
            sortOrder: sortField === 'profileName' && sortDirection,
            sorter: ( a, b ) => a.profileName.localeCompare( b.profileName )
        },
        {
            key: 'brandId',
            dataIndex: 'brandId',
            title: 'Brand',
            sortOrder: sortField === 'brandId' && sortDirection,
            render: ( i ) => {
                const brand = getBrand( i );
                return brand ? <ColoredTag.Brand key={ brand.id } brand={ brand } /> : null;
            },
            sorter: ( a, b ) => a.brandId.localeCompare( b.brandId )
        },
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: 'Created At',
            sortOrder: sortField === 'createdAt' && sortDirection,
            sorter: ( a, b ) => dayjs( a.createdAtFull ).isBefore( dayjs( b.createdAtFull ) ) ? -1 : 1
        },
        {
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            title: 'Updated At',
            sortOrder: sortField === 'updatedAt' && sortDirection,
            sorter: ( a, b ) => dayjs( a.updatedAtFull ).isBefore( dayjs( b.updatedAtFull ) ) ? -1 : 1
        },
        {
            key: 'actions', 
            title: 'Actions',
            fixed: 'right', 
            width: '100px',
            render: ( i ) => 
                <>
                    <Space>
                        <Button
                            type="link"
                            icon={ <EyeOutlined /> }
                            onClick={ () => handlePreviewClick( i.preview ) }
                        />

                        { flags.CERTIFICATES_OVERRIDE  &&
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> }
                                onClick={ () => handleEditClick( i ) }
                            />
                        }
                    </Space>
                </>
        } 
    ];

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return ( 
        <Layout className="certificate-list-page">
            
            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
             
            { /* Main Content */ }
            { !isLoading &&
                <Card
                    className="certificate-list-content"
                    title="Certificate Templates"
                    extra={ !hasError &&
                        <Button
                            icon={ <PlusOutlined /> }
                            onClick={ () => handleCreateClick() }
                            type="text"
                        >
                            Create New Template
                        </Button>
                    }
                >   
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }

                    { !hasError &&
                        <>
                            { /* Search bar */ }
                            <SearchBar
                                searchText={ searchText }
                                placeHolerText="Search by name"
                                handleSearchCallback={ handleSearchClick }
                            /> 

                            { /* Data table */ }
                            <Table
                                tableLayout="auto"
                                rowKey='id'
                                columns={ columns.map( c => c.key === 'name' ? c : { ...noWrap, ...c } ) }
                                dataSource={ filteredData || certificates }
                                onChange={ handleTableChange }
                                pagination={ {
                                    onChange: handlePaginatorChange,
                                    defaultCurrent: currentPage || 1,
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                            /> 
                        </>
                    }
                </Card>
            }
        </Layout>
    );
};

export default CertificatesListPage;
